import api from '../../store/api/api';
import {usePollingRecords} from "./usePollingManager";
import { useMemo, useState, useCallback } from 'react';

export const RIDE_STATUS = {
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending_approval',
    APPROVED: 'approved',
    WAITING_FOR_RIDER: 'waiting_for_rider',
    QUEUED_FOR_UBER: 'queued_for_uber',
    SENT_TO_UBER: 'sent_to_uber',
    UBER_ACTIVE: 'uber_active',
    UBER_COMPLETED: 'uber_completed',
    UBER_CANCELED: 'uber_canceled',
    CANCELED: 'canceled',
    RIDER_CANCELED: 'rider_canceled',
    FAILED: 'failed',
};

export function useUberRides(crewId, enabled = true) {
    const pollableStatuses = [RIDE_STATUS.QUEUED_FOR_UBER, RIDE_STATUS.SENT_TO_UBER, RIDE_STATUS.UBER_ACTIVE];
    const [submitError, setSubmitError] = useState(null);
    const [infoModal, setInfoModal] = useState({ show: false, data: null, loading: false });

    const {
        records: uberRidesMap,
        loading: loadingAllRides,
        updateRecord: updateRide,
        removeRecord: removeRide,
        startPolling,
        reload: reloadRides,
    } = usePollingRecords({
        enabled,
        loadRecords: async () => {
            if (!crewId) return [];
            const response = await api.get('/uber/ride-requests/', { crew_id: crewId });
            return response.data;
        },
        pollRecord: async (rideId) => {
            const response = await api.get('/uber/ride-requests/', { ride_request_id: rideId });
            return response.data[0];
        },
        shouldPollStatus: (status) => pollableStatuses.includes(status),
        // filterOnLoad: (record) => record,
        filterOnLoad: (record) => record.status !== RIDE_STATUS.CANCELED,
        pollInterval: 5000,
        idField: 'id',
    });

    const isReadyForUber = (ride) => {
        return (ride.status === RIDE_STATUS.APPROVED && ride.total_seat_count === ride.total_assigned_seats)
            || ride.status === RIDE_STATUS.FAILED;
    };

    const loadingRideIds = useMemo(() => {
        return new Set(
            Object.values(uberRidesMap)
                .filter(ride => ride.isLoading)
                .map(ride => ride.id)
        );
    }, [uberRidesMap]);

    const cancelRide = async (rideId) => {
        if (!window.confirm('Are you sure you want to cancel this ride request?')) {
            return;
        }

        updateRide(rideId, { isLoading: true });

        try {
            let response = await api.post(`/uber/ride-requests/${rideId}/cancel/`);
            updateRide(rideId, { ...response.data, isLoading: false });
            //removeRide(rideId);
        } catch (err) {
            if (err.response?.data?.error) {
                alert(err.response.data.error);
            } else {
                alert('Error cancelling ride. Check console.');
            }
            updateRide(rideId, { isLoading: false });
        }
    };

    const submitRide = async (rideId) => {
        setSubmitError(null);
        updateRide(rideId, { isLoading: true });

        try {
            const response = await api.post(`/uber/ride-requests/${rideId}/create-uber-trip/`);
            updateRide(rideId, { ...response.data, isLoading: false });

            if (pollableStatuses.includes(response.data.status)) {
                startPolling();
            }
        } catch (err) {
            console.error('Failed to create Uber trip', err);

            if (err.response?.status === 422) {
                const errorData = err.response.data;
                const errorMessage = errorData?.message
                    || errorData?.error
                    || errorData?.detail
                    || 'The ride cannot be submitted due to validation errors. Please check the ride details and try again.';

                const newStatus = errorData?.status;
                if (newStatus) {
                    updateRide(rideId, { status: newStatus, isLoading: false });
                } else {
                    updateRide(rideId, { isLoading: false });
                }

                setSubmitError(errorMessage);
                throw new Error(errorMessage);
            } else {
                const errorMessage = err.response?.data?.error
                    || err.response?.data?.message
                    || 'Error creating Uber trip. Please try again.';
                updateRide(rideId, { isLoading: false });
                throw new Error(errorMessage);
            }
        }
    };

    const approveRide = async (rideId, approvedBy) => {
        updateRide(rideId, { isLoading: true });

        try {
            const response = await api.post(`/uber/ride-requests/${rideId}/approve/`, {
                approved_by: approvedBy
            });
            updateRide(rideId, { ...response.data, isLoading: false });

            if (pollableStatuses.includes(response.data.status)) {
                startPolling();
            }
        } catch (err) {
            console.error('Failed to approve ride', err);
            const errorMessage = err.response?.data?.error || 'Error approving ride. Check console.';
            alert(errorMessage);
            updateRide(rideId, { isLoading: false });
        }
    };

    const clearSubmitError = () => {
        setSubmitError(null);
    };

    const fetchRideInfo = useCallback(async (rideId) => {
        setInfoModal({ show: true, data: null, loading: true });
        try {
            const response = await api.get(`/uber/ride-requests/${rideId}/get-ride-info`);
            setInfoModal({ show: true, data: response.data, loading: false });
        } catch (err) {
            console.error('Failed to load ride info', err);
            setInfoModal({ show: true, data: null, loading: false });
        }
    }, []);

    const closeRideInfo = useCallback(() => {
        setInfoModal({ show: false, data: null, loading: false });
    }, []);

    return {
        uberRides: Object.values(uberRidesMap).map((ride) => ({
            ...ride,
            readyForUber: isReadyForUber(ride),
        })),
        loadingAllRides,
        loadingRideIds,
        cancelRide,
        submitRide,
        approveRide,
        reloadRides,
        submitError,
        clearSubmitError,
        infoModal,
        fetchRideInfo,
        closeRideInfo,
    };
}